import React, { useRef, useState } from 'react';

import { ActionIcon, Flex, Paper, PaperProps, Text, Textarea, useMantineTheme } from '@mantine/core';
import { FileWithPath } from '@mantine/dropzone';
import { useForm } from '@mantine/form';
import { IconMicrophone, IconPhoto } from '@tabler/icons-react';
import { CharacterCounter } from 'components/CharacterCounter';
import { ChatActionButton } from 'components/SmartAssistantComponents/ChatActionButton';
import { SmartAssistantChatAttachmentAudioHandler } from 'components/SmartAssistantComponents/SmartAssistantChatAudioAttachmentHandler';
import { SmartAssistantChatImageAttachmentDropzone } from 'components/SmartAssistantComponents/SmartAssistantChatImageAttachmentDropzone';
import { SentIcon } from 'hugeicons-react';
import { useTranslation } from 'react-i18next';
import { ChatAttachment, ChatMessage } from 'shared/interfaces/ChatMessage';
import { ChatAttachmentConfiguration, SmartAssistantVisualConfig } from 'shared/interfaces/SmartAssistant';

const ERROR_MESSAGE_TIMEOUT_MS = 3000;

interface SmartAssistantChatInputProps extends PaperProps {
  onSend: (chatMessage: ChatMessage) => void;
  isSendDisabled: boolean;
  isInputDisabled: boolean;
  smartAssistantVisualConfig: SmartAssistantVisualConfig;
  isPreview?: boolean;
  disableInputZoomingWithinIframe?: boolean;
  maxCharacterCount?: number;
  smartAssistantId?: string;
  conversationId?: string;
  chatAttachmentConfiguration?: ChatAttachmentConfiguration;
  isGuest?: boolean;
}

export function SmartAssistantChatInput({
  onSend,
  isSendDisabled,
  isInputDisabled,
  smartAssistantVisualConfig,
  disableInputZoomingWithinIframe,
  maxCharacterCount,
  smartAssistantId,
  conversationId,
  chatAttachmentConfiguration,
  isGuest = false,
  ...paperProps
}: SmartAssistantChatInputProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const dropzoneOpenRef = useRef<() => void>(null);
  const imageAttachmentDropzoneRef = useRef<{ clear: () => void; onDrop: (files: FileWithPath[]) => void }>(null);
  const [focus, setFocus] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isMicrophoneDisabled, setIsMicrophoneDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    conversationBackgroundColor: backgroundColor,
    userMessageColor: accentColor,
    assistantMessageColor,
    secondaryBackgroundColor: borderColor
  } = smartAssistantVisualConfig;

  const userInputForm = useForm<{ text: string; images: ChatAttachment[] }>({
    initialValues: { text: '', images: [] }
  });

  // We do not use userInputForm.validateField or userInputForm.validate here because
  // calling those methods in the render body would trigger a state update and cause
  // an infinite re-render loop. Instead, we replicate the validation logic in a pure way.
  const isTextValid =
    userInputForm.values.text.trim().length > 0 &&
    (!maxCharacterCount || userInputForm.values.text.length <= maxCharacterCount);
  const isAImagesValid = userInputForm.values.images.length > 0;

  const isInputValid = isTextValid || isAImagesValid;

  const sendMessage = () => {
    if (!isInputValid) return;
    onSend({
      text: userInputForm.values.text,
      fromUser: true,
      attachments: userInputForm.values.images
    });
    userInputForm.setValues({ text: '', images: [] });
    imageAttachmentDropzoneRef.current?.clear();
  };
  const cannotSendMessage = isSendDisabled || !isInputValid;
  const textColor = theme.other.getReadableColor(backgroundColor);
  const disabledColor = theme.other.getDimmedColor(assistantMessageColor, 0.4);
  const disabledTextColor = theme.other.getDimmedColor(theme.other.getReadableColor(disabledColor), 0.8);

  const displayTemporaryErrorMessage = (errorMessage: string | null) => {
    setErrorMessage(errorMessage);
    setTimeout(() => {
      setErrorMessage(null);
    }, ERROR_MESSAGE_TIMEOUT_MS);
  };

  const handleImagePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!chatAttachmentConfiguration?.isImageEnabled) return;
    const clipboardData = event.clipboardData;
    const items = clipboardData.items;
    const imageItems = Array.from(items).filter((item) => item.type.startsWith('image/'));

    if (imageItems.length < 1) return;

    event.preventDefault();

    const files: FileWithPath[] = [];
    imageItems.forEach((item) => {
      const file = item.getAsFile();
      if (file) files.push(new File([file], file.name, { type: file.type }));
    });

    if (files.length > 0) imageAttachmentDropzoneRef.current?.onDrop(files);
  };

  return (
    <>
      <Paper
        withBorder
        py="4px"
        pl="8px"
        pr="4px"
        bg={backgroundColor}
        sx={{
          borderRadius: '8px',
          borderColor: `${focus ? accentColor : borderColor} !important`
        }}
        {...paperProps}>
        <SmartAssistantChatImageAttachmentDropzone
          ref={imageAttachmentDropzoneRef}
          smartAssistantId={smartAssistantId}
          conversationId={conversationId}
          setImages={(imgs) => userInputForm.setFieldValue('images', imgs)}
          dropzoneOpenRef={dropzoneOpenRef}
          enabled={chatAttachmentConfiguration?.isImageEnabled}
          isGuest={isGuest}
          setErrorMessage={displayTemporaryErrorMessage}>
          <form onSubmit={() => sendMessage()}>
            <Flex
              direction={
                chatAttachmentConfiguration?.isAudioEnabled || chatAttachmentConfiguration?.isImageEnabled
                  ? 'column'
                  : 'row'
              }
              w="100%">
              <Textarea
                disabled={isInputDisabled || isRecording}
                w="100%"
                placeholder={t('SMART_ASSISTANT.TEXT_PLACEHOLDER')}
                autosize
                variant="unstyled"
                minRows={1}
                maxRows={10}
                onKeyDown={(event) => {
                  if (event.code === 'Enter' && !event.shiftKey && !cannotSendMessage) {
                    sendMessage();
                    event.preventDefault();
                  }
                }}
                {...userInputForm.getInputProps('text')}
                onPaste={handleImagePaste}
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
                sx={{
                  paddingBottom: 0,
                  paddingTop: 0,
                  '.mantine-Textarea-input': {
                    // This is set to disable the zooming on input fields on mobile devices.
                    // Zooming will be disabled if font size is set to 16px or more.
                    // Normally, using user-scalable=no in the meta tag will disable zooming on input fields.
                    // However, this is not working from within an iframe because modern mobile browsers ignore viewport setting for zooming for iframes.
                    fontSize: disableInputZoomingWithinIframe ? '16px' : undefined,
                    color: textColor,
                    backgroundColor: backgroundColor,
                    '&:disabled': {
                      backgroundColor: backgroundColor,
                      color: textColor
                    }
                  },
                  '.mantine-Textarea-input::placeholder': {
                    color: textColor,
                    opacity: 0.5
                  },
                  '.mantine-Textarea-input::-webkit-scrollbar': {
                    width: '6px',
                    height: '6px'
                  },
                  '.mantine-Textarea-input::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.colors.dark[2],
                    borderRadius: '10px'
                  },
                  '.mantine-Textarea-input::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent'
                  }
                }}
              />
              {isRecording && chatAttachmentConfiguration?.isAudioEnabled && smartAssistantId ? (
                <SmartAssistantChatAttachmentAudioHandler
                  w="100%"
                  smartAssistantId={smartAssistantId}
                  smartAssistantVisualConfig={smartAssistantVisualConfig}
                  setIsRecording={setIsRecording}
                  onTranscription={(transcription) => {
                    userInputForm.setFieldValue('text', `${userInputForm.values.text.trim()} ${transcription.trim()}`);
                  }}
                  setIsMicrophoneDisabled={setIsMicrophoneDisabled}
                  isGuest={isGuest}
                  setErrorMessage={displayTemporaryErrorMessage}
                />
              ) : (
                <Flex
                  direction="row"
                  align="center"
                  justify={
                    chatAttachmentConfiguration?.isAudioEnabled || chatAttachmentConfiguration?.isImageEnabled
                      ? 'space-between'
                      : 'flex-end'
                  }>
                  <Flex direction="row" align="center" gap="xs">
                    {chatAttachmentConfiguration?.isImageEnabled && (
                      <ChatActionButton
                        bg={smartAssistantVisualConfig.conversationBackgroundColor}
                        onClick={() => dropzoneOpenRef.current?.()}>
                        <IconPhoto size={18} color={textColor} />
                      </ChatActionButton>
                    )}

                    {chatAttachmentConfiguration?.isAudioEnabled && (
                      <ChatActionButton
                        bg={smartAssistantVisualConfig.conversationBackgroundColor}
                        disabled={isMicrophoneDisabled}
                        onClick={() => !isMicrophoneDisabled && setIsRecording(true)}>
                        <IconMicrophone size={18} color={textColor} />
                      </ChatActionButton>
                    )}

                    {errorMessage && (
                      <Text size="xs" color="red">
                        {t(errorMessage)}
                      </Text>
                    )}
                  </Flex>
                  <ActionIcon
                    variant="filled"
                    size={38}
                    onClick={() => sendMessage()}
                    disabled={Boolean(cannotSendMessage)}
                    sx={{
                      backgroundColor: accentColor,
                      border: 'none',
                      color: theme.other.getReadableColor(accentColor),
                      '&:hover': {
                        backgroundColor: accentColor,
                        opacity: 0.9
                      },
                      '&:disabled': {
                        backgroundColor: disabledColor,
                        color: disabledTextColor
                      }
                    }}>
                    <SentIcon size={18} />
                  </ActionIcon>
                </Flex>
              )}
            </Flex>
          </form>
        </SmartAssistantChatImageAttachmentDropzone>
      </Paper>
      {/* We only want to show the CharacterCounter when text exceeds the max character count since it does not happen often */}
      {maxCharacterCount && userInputForm.values.text.length > maxCharacterCount && (
        <CharacterCounter length={userInputForm.values.text.length} maxLength={maxCharacterCount} />
      )}
    </>
  );
}
